import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SendEmailContact {
  name: string;
  email: string;
  initials: string;
  color: string;
}

export interface SenderAccount {
  id: string;
  name: string;
  email: string;
  provider: "gmail" | "outlook" | "other";
}

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: () => void;
  docType: "Quotation" | "Invoice";
  docNumber: string;
  contacts: SendEmailContact[];
  senderAccounts: SenderAccount[];
}

// ─── Mock sender accounts (replace with user-connected accounts from API) ─────

export const MOCK_SENDER_ACCOUNTS: SenderAccount[] = [
  { id: "sa-1", name: "James Mcdonald", email: "james@makula.io",       provider: "gmail"   },
  { id: "sa-2", name: "James Mcdonald", email: "j.mcdonald@acme.com",   provider: "outlook" },
  { id: "sa-3", name: "James Mcdonald", email: "jmcdonald@company.org", provider: "other"   },
];

// ─── Provider Icons ───────────────────────────────────────────────────────────

function GmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect width="16" height="16" rx="3" fill="#fff" />
      <path d="M2 4.5v7A1.5 1.5 0 003.5 13H5V7.2L8 9.5l3-2.3V13h1.5A1.5 1.5 0 0014 11.5v-7L8 8.5 2 4.5z" fill="#EA4335" />
      <path d="M2 4.5L8 8.5l6-4A1.5 1.5 0 0012.5 3h-9A1.5 1.5 0 002 4.5z" fill="#4285F4" />
      <path d="M5 7.2V13h3V9.5L5 7.2z" fill="#34A853" />
      <path d="M11 7.2L8 9.5V13h3V7.2z" fill="#FBBC05" />
    </svg>
  );
}

function OutlookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect width="16" height="16" rx="3" fill="#0078D4" />
      <rect x="8.5" y="4" width="5" height="8" rx="1" fill="#fff" fillOpacity="0.9" />
      <path d="M9 5.5h4M9 7.5h4M9 9.5h2.5" stroke="#0078D4" strokeWidth="0.8" strokeLinecap="round" />
      <rect x="2" y="5" width="6" height="6" rx="1.5" fill="#fff" />
      <ellipse cx="5" cy="8" rx="1.8" ry="2" fill="#0078D4" />
    </svg>
  );
}

function GenericEmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect width="16" height="16" rx="3" fill="#F3F4F6" />
      <rect x="2.5" y="4.5" width="11" height="7" rx="1.5" stroke="#748094" strokeWidth="1.1" />
      <path d="M2.5 6l5.5 3.5L13.5 6" stroke="#748094" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProviderIcon({ provider }: { provider: SenderAccount["provider"] }) {
  if (provider === "gmail")   return <GmailIcon />;
  if (provider === "outlook") return <OutlookIcon />;
  return <GenericEmailIcon />;
}

function ProviderLabel({ provider }: { provider: SenderAccount["provider"] }) {
  if (provider === "gmail")   return "Gmail";
  if (provider === "outlook") return "Outlook";
  return "Email";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const LABEL_STYLE: React.CSSProperties = {
  display: "block", fontSize: 13, fontWeight: 600,
  color: "#172B4D", marginBottom: 6, fontFamily: "var(--font-family-base)",
};

function buildMessage(
  docType: "Quotation" | "Invoice",
  docNumber: string,
  firstName: string,
  senderName: string,
): string {
  const prefix = docType === "Quotation" ? "q" : "inv";
  const portalUrl = `https://portal.makula.io/${prefix}/${docNumber}`;
  if (docType === "Quotation") {
    return `Dear ${firstName},\n\nPlease find attached your quotation ${docNumber}.\n\nThis quotation outlines the scope of work, pricing, and terms we discussed. Kindly review and let us know if you have any questions.\n\nView and approve your quotation online:\n${portalUrl}\n\nWe look forward to working with you.\n\nBest regards,\n${senderName}`;
  }
  return `Dear ${firstName},\n\nPlease find attached Invoice ${docNumber} for your review and payment.\n\nKindly arrange payment by the due date as per the agreed payment terms.\n\nView your invoice online:\n${portalUrl}\n\nShould you have any questions, please don't hesitate to reach out.\n\nBest regards,\n${senderName}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SendEmailModal({
  isOpen,
  onClose,
  onSend,
  docType,
  docNumber,
  contacts,
  senderAccounts,
}: SendEmailModalProps) {
  const [contactIdx, setContactIdx]     = React.useState(0);
  const [senderIdx, setSenderIdx]       = React.useState(0);
  const [showContactDrop, setShowContactDrop] = React.useState(false);
  const [showSenderDrop, setShowSenderDrop]   = React.useState(false);
  const [subject, setSubject] = React.useState(`${docType} ${docNumber}`);
  const [message, setMessage] = React.useState(() =>
    buildMessage(docType, docNumber, contacts[0]?.name.split(" ")[0] ?? "", senderAccounts[0]?.name ?? "")
  );

  const contactDropRef = React.useRef<HTMLDivElement>(null);
  const senderDropRef  = React.useRef<HTMLDivElement>(null);

  const selectedContact = contacts[contactIdx]     ?? contacts[0];
  const selectedSender  = senderAccounts[senderIdx] ?? senderAccounts[0];

  // Reset on open
  React.useEffect(() => {
    if (isOpen) {
      setContactIdx(0);
      setSenderIdx(0);
      setSubject(`${docType} ${docNumber}`);
      setMessage(buildMessage(docType, docNumber, contacts[0]?.name.split(" ")[0] ?? "", senderAccounts[0]?.name ?? ""));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docNumber]);

  // Rebuild message when contact or sender changes
  React.useEffect(() => {
    if (selectedContact && selectedSender) {
      setMessage(buildMessage(docType, docNumber, selectedContact.name.split(" ")[0], selectedSender.name));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactIdx, senderIdx]);

  // Close dropdowns on outside click
  React.useEffect(() => {
    function onOut(e: MouseEvent) {
      if (contactDropRef.current && !contactDropRef.current.contains(e.target as Node)) setShowContactDrop(false);
      if (senderDropRef.current  && !senderDropRef.current.contains(e.target as Node))  setShowSenderDrop(false);
    }
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  if (!isOpen) return null;

  const canSend = Boolean(selectedContact && selectedSender && subject);

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(23,43,77,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 540, boxShadow: "0 24px 64px rgba(23,43,77,0.18)", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 24px 18px", flexShrink: 0 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.3px" }}>
            Send {docType}
          </span>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid #E8EAED", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M2 2l10 10M12 2L2 12" />
            </svg>
          </button>
        </div>

        <div style={{ height: 1, background: "#E8EAED", flexShrink: 0 }} />

        {/* Scrollable body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto" }}>

          {/* ── Contact selector ── */}
          <div>
            <label style={LABEL_STYLE}>Contact</label>
            <div ref={contactDropRef} style={{ position: "relative" }}>
              <button
                onClick={() => contacts.length > 1 && setShowContactDrop((o) => !o)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", cursor: contacts.length > 1 ? "pointer" : "default", textAlign: "left" }}
              >
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: selectedContact?.color ?? "#9333EA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "var(--font-family-base)" }}>{selectedContact?.initials ?? ""}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedContact?.name ?? ""}</div>
                  <div style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedContact?.email ?? ""}</div>
                </div>
                {contacts.length > 1 && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" style={{ flexShrink: 0 }}>
                    <path d="M3.5 5.5l3.5 3.5 3.5-3.5" />
                  </svg>
                )}
              </button>
              {showContactDrop && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: "#fff", border: "1px solid #E8EAED", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", overflow: "hidden" }}>
                  {contacts.map((c, i) => (
                    <button
                      key={i}
                      onClick={() => { setContactIdx(i); setShowContactDrop(false); }}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", border: "none", background: i === contactIdx ? "#F0F4FF" : "#fff", cursor: "pointer" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = i === contactIdx ? "#F0F4FF" : "#fff")}
                    >
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: c.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", fontFamily: "var(--font-family-base)" }}>{c.initials}</span>
                      </div>
                      <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                        <div style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</div>
                      </div>
                      {i === contactIdx && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.8" strokeLinecap="round" style={{ flexShrink: 0 }}>
                          <path d="M2 6l3 3 5-5" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Sender Email dropdown ── */}
          <div>
            <label style={LABEL_STYLE}>Sender Email</label>
            <div ref={senderDropRef} style={{ position: "relative" }}>
              <button
                onClick={() => senderAccounts.length > 1 && setShowSenderDrop((o) => !o)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", cursor: senderAccounts.length > 1 ? "pointer" : "default", textAlign: "left" }}
              >
                {/* Provider icon */}
                <div style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #E8EAED", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "#FAFAFA" }}>
                  <ProviderIcon provider={selectedSender?.provider ?? "other"} />
                </div>
                {/* Name + email */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedSender?.email ?? ""}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
                    <ProviderLabel provider={selectedSender?.provider ?? "other"} /> · {selectedSender?.name ?? ""}
                  </div>
                </div>
                {senderAccounts.length > 1 && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" style={{ flexShrink: 0 }}>
                    <path d="M3.5 5.5l3.5 3.5 3.5-3.5" />
                  </svg>
                )}
              </button>

              {showSenderDrop && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: "#fff", border: "1px solid #E8EAED", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", overflow: "hidden" }}>
                  {senderAccounts.map((acc, i) => (
                    <button
                      key={acc.id}
                      onClick={() => { setSenderIdx(i); setShowSenderDrop(false); }}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", border: "none", background: i === senderIdx ? "#F0F4FF" : "#fff", cursor: "pointer" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = i === senderIdx ? "#F0F4FF" : "#fff")}
                    >
                      <div style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #E8EAED", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "#FAFAFA" }}>
                        <ProviderIcon provider={acc.provider} />
                      </div>
                      <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{acc.email}</div>
                        <div style={{ fontSize: 12, color: "#6B7280", fontFamily: "var(--font-family-base)" }}>
                          <ProviderLabel provider={acc.provider} /> · {acc.name}
                        </div>
                      </div>
                      {i === senderIdx && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.8" strokeLinecap="round" style={{ flexShrink: 0 }}>
                          <path d="M2 6l3 3 5-5" />
                        </svg>
                      )}
                    </button>
                  ))}

                  {/* Connect more accounts CTA */}
                  <div style={{ borderTop: "1px solid #E8EAED" }}>
                    <button
                      onClick={() => setShowSenderDrop(false)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", border: "none", background: "#fff", cursor: "pointer", color: "#0517F8" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M7 2v10M2 7h10" />
                      </svg>
                      <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-family-base)" }}>Connect email account</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Subject ── */}
          <div>
            <label style={LABEL_STYLE}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={{ width: "100%", height: 44, padding: "0 14px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", fontFamily: "var(--font-family-base)", fontSize: 14, color: "#172B4D", outline: "none", boxSizing: "border-box" }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#0517F8")}
              onBlur={(e)  => (e.currentTarget.style.borderColor = "#E8EAED")}
            />
          </div>

          {/* ── Message ── */}
          <div>
            <label style={LABEL_STYLE}>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", fontFamily: "var(--font-family-base)", fontSize: 13, color: "#172B4D", outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: "1.6" }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#0517F8")}
              onBlur={(e)  => (e.currentTarget.style.borderColor = "#E8EAED")}
            />
          </div>
        </div>

        <div style={{ height: 1, background: "#E8EAED", flexShrink: 0 }} />

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "14px 24px", flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ height: 38, padding: "0 18px", border: "1.5px solid #E8EAED", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 600, color: "#172B4D" }}
          >
            Cancel
          </button>
          <button
            onClick={() => { onSend(); onClose(); }}
            disabled={!canSend}
            style={{ height: 38, padding: "0 18px", border: "none", borderRadius: 8, background: canSend ? "#0517F8" : "#E8EAED", cursor: canSend ? "pointer" : "not-allowed", fontFamily: "var(--font-family-base)", fontSize: 13, fontWeight: 700, color: canSend ? "#fff" : "#9CA3AF", display: "flex", alignItems: "center", gap: 6 }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M12.5 1.5L1.5 6l4.5 2 2 4.5 4.5-11z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 7l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Send email
          </button>
        </div>
      </div>
    </div>
  );
}
